/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { IDBPObjectStore } from 'idb';
import { ActionsObservable, combineEpics } from 'redux-observable';
import { empty, from, merge, throwError } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, concatMap, exhaustMap, expand, filter, finalize, map, reduce, switchMap, take, tap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import { AppAction, fetchTokenActions, getCoops, getFacilities } from '..';
import config from '../../config';
import { DairyFARMDB, getDB, storeNames } from '../../db/store';
import { COOPS_STORE } from '../../db/stores/coops';
import { FACILITIES_STORE } from '../../db/stores/facilities';
import { createEpic } from '../../utils/epics';
import { loadToken } from './../../utils/token';

const { IMAGE_STORE } = storeNames;
const resurrectImagesKey = 'flag_20200123_image_restore';
const migrateFacilityDataKey = 'flag_20200512_facility_data_migrate';

// HACK: remove ASAP
const tmpImageRestoreFix = createEpic((action$: ActionsObservable<AppAction>) => {
  if (localStorage.getItem(resurrectImagesKey) === 'true') {
    return empty();
  }
  return action$.pipe(filter(isActionOf(fetchTokenActions.success))).pipe(
    exhaustMap(() => {
      return from(
        getDB().then((db) =>
          db
            .transaction(IMAGE_STORE, 'readonly')
            .objectStore(IMAGE_STORE)
            .openCursor()
        )
      ).pipe(
        expand((cursor) => (cursor === null ? empty() : from(cursor.continue()))),
        filter((cursor) => cursor !== null && cursor.value !== null),
        map((cursor) => cursor!.value),
        concatMap((value) => {
          const token = loadToken();
          if (token === null) {
            return throwError('Token not found!');
          }
          const id = value.id;
          const uri = value.uri;
          const byteString = atob(uri.split(',')[1]);
          const bytes = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) {
            bytes[i] = byteString.charCodeAt(i);
          }
          const mimeType = uri
            .split(',')[0]
            .split(':')[1]
            .split(';')[0];
          const blob = new Blob([bytes], { type: mimeType });

          const formData = new FormData();
          formData.append('mime_type', mimeType);
          formData.append('attachment', blob);

          return ajax.put(`${config.dfdmHost}/api/attachments/${id}`, formData, {
            Authorization: `Bearer ${token!.access_token}`,
          });
        }),
        // reduce() is used to wait for the preceeding observables to fully finish
        // before continuing to tap(). We don't care about the actual results of the
        // AJAX requests, since any failures will trigger an error and cause reduce(),
        // tap(), and switchMap() to be skipped.
        reduce(() => 1, 1),
        tap(() => localStorage.setItem(resurrectImagesKey, 'true')),
        switchMap(() => empty()),
        catchError(() => empty())
      );
    })
  );
});

function clearStore(objectStore: IDBPObjectStore<DairyFARMDB, any, any>) {
  return objectStore.openCursor().then(function deleteItem(cursor) {
    if (cursor) {
      cursor
        .delete()
        .then(() => cursor.continue())
        .then(deleteItem);
    }
    return null;
  });
}

const moveFacilityDataToRedux = createEpic((action$) =>
  action$.pipe(
    take(1),
    filter(() => {
      if (localStorage.getItem(migrateFacilityDataKey) === 'true') {
        return false;
      }
      return true;
    }),
    switchMap(() => from(getDB())),
    switchMap((db) => {
      const tx = db.transaction([FACILITIES_STORE, COOPS_STORE], 'readwrite');
      const facilitiesStore = tx.objectStore(FACILITIES_STORE);
      const coopsStore = tx.objectStore(COOPS_STORE);

      // We don't need to take up double the space in indexeddb sources, so empty one when populating the other
      return merge(
        from(
          facilitiesStore
            .getAll()
            .then((facilities) => clearStore(facilitiesStore).then(() => facilities))
            .then(getFacilities.success)
        ),
        from(
          coopsStore
            .getAll()
            .then((coops) => clearStore(coopsStore).then(() => coops))
            .then(getCoops.success)
        )
      );
    }),
    finalize(() => localStorage.setItem(migrateFacilityDataKey, 'true'))
  )
);

export const oneTimeEpics = combineEpics(tmpImageRestoreFix, moveFacilityDataToRedux);
